import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
  MenuItem,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import categoryService from "../../repository/categoryRepository";

const SubcategoryModal = ({ open, onClose, onSave, initialValues = {} }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // console.log(initialValues);

  const [subcategoryName, setSubcategoryName] = useState("");
  const [sizeType, setSizeType] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(null);
  const [iconName, setIconName] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    setSubcategoryName(initialValues.subcategoryName || "");
    setSizeType(initialValues.sizeType || "");
    setDescription(initialValues.description || "");
    setSubcategoryId(initialValues.subcategoryId || "");
    setIcon(null);
    setIconName("");
  }, [initialValues]);

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setIcon(file);
    setIconName(file ? file.name : "");
  };

  const handleSaveChanges = async () => {
    try {
      const formData = new FormData();
      formData.append("subCategoryName", subcategoryName);
      formData.append("sizeType", sizeType);
      formData.append("description", description);
      formData.append("subCategoryId", subcategoryId);

      if (icon) {
        formData.append("imageURL", icon);
        formData.append("isImage", true);
      }

      const response = await categoryService.editSubCategory(formData);

      if (response.success) {
        setSnackbarMessage("Subcategory updated successfully!");
        setSnackbarSeverity("success");
        if (onSave && typeof onSave === "function") {
          onSave(response.data);
        }
        onClose();
      } else {
        setSnackbarMessage(
          response.status === 400
            ? "Please try again."
            : "Error saving subcategory.",
        );
        setSnackbarSeverity(response.status === 400 ? "warning" : "error");
      }
    } catch (error) {
      setSnackbarMessage("Error saving subcategory: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary || theme.palette.primary.main,
            color:
              theme.palette.mode === "light"
                ? "black"
                : colors.light || theme.palette.grey[100],
            position: "relative",
          }}
        >
          Edit Subcategory
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.grey || theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Subcategory Name"
            type="text"
            fullWidth
            variant="outlined"
            value={subcategoryName}
            onChange={(e) => setSubcategoryName(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
              "& .MuiInputLabel-root": {
                color: colors.text || theme.palette.text.primary,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.text || theme.palette.text.primary,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            fullWidth
            select
            label="Select Size Type"
            value={sizeType}
            onChange={(e) => setSizeType(e.target.value)}
            sx={{
              mb: "10px",
            }}
          >
            <MenuItem value="Roman (S, M, L, XL)">
              Roman Sizes (S, M, L, XL)
            </MenuItem>
            <MenuItem value="Number (28, 30, 40)">
              Number Sizes (28, 30, 40)
            </MenuItem>
            <MenuItem value="Dual Size (Upper and Lower)">
              Dual Size (Upper and Lower)
            </MenuItem>
            <MenuItem value="No Size">No Size</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
              "& .MuiInputLabel-root": {
                color: colors.text || theme.palette.text.primary,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.text || theme.palette.text.primary,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
              },
            }}
          />
          <Box mb={2}>
            <Button
              variant="contained"
              component="label"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              sx={{
                mb: "10px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.secondary || theme.palette.secondary.main
                    : colors.secondaryLight || theme.palette.secondary.light,
                color: "black",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? colors.secondary || theme.palette.secondary.main
                      : colors.secondaryLight || theme.palette.secondary.light,
                },
              }}
            >
              Upload Icon
              <input
                type="file"
                name="icon"
                hidden
                onChange={handleIconUpload}
              />
            </Button>
            {iconName && (
              <Typography
                variant="body2"
                color={(theme) => theme.palette.text.primary}
              >
                Uploaded: {iconName}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveChanges}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.greenAccent || theme.palette.success.main
                  : colors.blueAccent || theme.palette.primary.main,
              color: "black",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.greenAccent || theme.palette.success.main
                    : colors.blueAccent || theme.palette.primary.main,
              },
            }}
          >
            Update Subcategory
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          margin: 2,
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.snackbarLightBackground
              : colors.snackbarDarkBackground,
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            backgroundColor:
              snackbarSeverity === "success"
                ? colors.greenAccent
                : colors.redAccent || theme.palette.error.main,
            color:
              theme.palette.mode === "light"
                ? colors.snackbarLightText
                : colors.light || theme.palette.grey[100],
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubcategoryModal;
